import { useCallback, useMemo, useState } from 'react';
import { useOptionalDock, type DockApi } from '../dock/context';
import { sanitizeView, type ViewSettings } from './view';

export const VARIABLES_WINDOW = 'variables';

/**
 * The Variables window's view settings (spec 6.10.17), kept in the dock layout's `views.variables` so they
 * persist per user and survive the window being hidden. Without a dock (component tests) they are local.
 */
export function useVariablesView(): [
  ViewSettings,
  (update: (current: ViewSettings) => ViewSettings) => void,
] {
  const dock = useOptionalDock();
  const [local, setLocal] = useState<ViewSettings>(() => sanitizeView(null));
  const stored = dock?.state.layout.views[VARIABLES_WINDOW];
  const settings = useMemo(() => (dock ? sanitizeView(stored) : local), [dock, stored, local]);
  const update = useCallback(
    (change: (current: ViewSettings) => ViewSettings) => {
      if (dock) dock.setView(VARIABLES_WINDOW, change(settings));
      else setLocal(change);
    },
    [dock, settings],
  );
  return [settings, update];
}

/** Switches the window's view from a page (for example when the vault node is selected). */
export function setVariablesView(dock: DockApi | null, view: ViewSettings['view']) {
  if (!dock) return;
  const current = sanitizeView(dock.state.layout.views[VARIABLES_WINDOW]);
  if (current.view !== view) dock.setView(VARIABLES_WINDOW, { ...current, view });
}
